import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { defaultNames } from '../data/defaultNames'
import type { Entry } from '../types/raffle'
import { targetRotation } from '../utils/wheel'
import { loadEntries, loadHistory, saveEntries, saveHistory } from '../utils/storage'

let uid = 0
export const makeEntry = (name: string): Entry => ({
  id: `e${Date.now().toString(36)}${(++uid).toString(36)}`,
  name,
  drawn: false,
})

export const WHEEL_LIMIT = 20

interface UseDrawOptions {
  removeWinners: boolean
  boardMode: 'auto' | 'wheel' | 'tiles'
  onWinner?: (name: string) => void
  /** Called when the host puts winners back, so the live room can reset too. */
  onReset?: () => void
}

export function useDraw({ removeWinners, boardMode, onWinner, onReset }: UseDrawOptions) {
  const [entries, setEntries] = useState<Entry[]>(
    () => loadEntries() ?? defaultNames.map(makeEntry),
  )
  const [history, setHistory] = useState<Entry[]>(() => loadHistory())
  const [rotation, setRotation] = useState(0)
  const [spinning, setSpinning] = useState(false)
  const [pendingId, setPendingId] = useState<string | null>(null)
  const [pendingIndex, setPendingIndex] = useState<number | null>(null)
  const [spinToken, setSpinToken] = useState(0)
  const [winner, setWinner] = useState<Entry | null>(null)
  const [queuedSpin, setQueuedSpin] = useState(false)

  const winnerCb = useRef(onWinner)
  winnerCb.current = onWinner
  const resetCb = useRef(onReset)
  resetCb.current = onReset

  useEffect(() => saveEntries(entries), [entries])
  useEffect(() => saveHistory(history), [history])

  const pool = useMemo(
    () => (removeWinners ? entries.filter((e) => !e.drawn) : entries),
    [entries, removeWinners],
  )
  const drawnCount = entries.filter((e) => e.drawn).length
  const canSpin = pool.length >= 2
  const useTiles =
    boardMode === 'tiles' || (boardMode === 'auto' && pool.length > WHEEL_LIMIT)

  const spin = useCallback(() => {
    if (!canSpin || spinning) return
    const index = Math.floor(Math.random() * pool.length)
    setPendingId(pool[index].id)
    setPendingIndex(index)
    if (useTiles) {
      setSpinToken((t) => t + 1)
    } else {
      setRotation((r) => targetRotation(r, index, pool.length))
    }
    setSpinning(true)
  }, [canSpin, pool, spinning, useTiles])

  const handleSpinComplete = useCallback(() => {
    if (!pendingId) return
    const picked = entries.find((e) => e.id === pendingId)
    setSpinning(false)
    setPendingId(null)
    setPendingIndex(null)
    if (!picked) return
    setWinner(picked)
    setHistory((h) => [picked, ...h])
    winnerCb.current?.(picked.name)
  }, [entries, pendingId])

  const commitWinner = useCallback(() => {
    if (!winner) return
    if (removeWinners) {
      setEntries((prev) =>
        prev.map((e) => (e.id === winner.id ? { ...e, drawn: true } : e)),
      )
    }
    setWinner(null)
  }, [removeWinners, winner])

  const spinAgain = useCallback(() => {
    commitWinner()
    setQueuedSpin(true)
  }, [commitWinner])

  useEffect(() => {
    if (!queuedSpin || winner) return
    const t = window.setTimeout(() => {
      setQueuedSpin(false)
      spin()
    }, 220)
    return () => window.clearTimeout(t)
  }, [queuedSpin, winner, spin])

  const addNames = useCallback((names: string[]) => {
    setEntries((prev) => {
      const seen = new Set(prev.map((e) => e.name.toLowerCase()))
      const fresh = names.filter((n) => {
        const key = n.toLowerCase()
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
      return [...prev, ...fresh.map(makeEntry)]
    })
  }, [])

  const removeEntry = useCallback((id: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== id))
  }, [])

  const resetWinners = useCallback(() => {
    setEntries((prev) => prev.map((e) => ({ ...e, drawn: false })))
    setHistory([])
    setWinner(null)
    resetCb.current?.()
  }, [])

  const clearAll = useCallback(() => {
    setEntries([])
    setHistory([])
    setWinner(null)
    resetCb.current?.()
  }, [])

  return {
    entries,
    history,
    pool,
    drawnCount,
    canSpin,
    useTiles,
    spinning,
    rotation,
    pendingIndex,
    spinToken,
    winner,
    spin,
    handleSpinComplete,
    commitWinner,
    spinAgain,
    addNames,
    removeEntry,
    resetWinners,
    clearAll,
  }
}
